/**
 * SignalR connection provider for real-time portal events.
 * Manages connection lifecycle, reconnection with exponential backoff,
 * and dispatches events to React Query cache invalidation.
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import {
  HubConnectionBuilder,
  LogLevel,
  type HubConnection,
} from "@microsoft/signalr";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";

export type SignalRConnectionState = "connected" | "reconnecting" | "disconnected";

interface SignalRContextValue {
  readonly connectionState: SignalRConnectionState;
}

const SignalRContext = createContext<SignalRContextValue>({
  connectionState: "disconnected",
});

const SIGNALR_HUB_URL =
  (import.meta.env.VITE_SIGNALR_HUB_URL as string | undefined) ??
  `${(import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ""}/api`;

interface SignalRProviderProps {
  readonly children: ReactNode;
}

export function SignalRProvider({ children }: SignalRProviderProps) {
  const [connectionState, setConnectionState] =
    useState<SignalRConnectionState>("disconnected");
  const connectionRef = useRef<HubConnection | null>(null);
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading, userId, getAccessToken } = useAuth();

  const handlePIMSessionStatusChanged = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["pim"] });
    void queryClient.invalidateQueries({ queryKey: ["dashboard", "stats"] });
  }, [queryClient]);

  const handleGDAPRelationshipStatusChanged = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["gdap"] });
  }, [queryClient]);

  const handleSystemHealthChanged = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["health"] });
  }, [queryClient]);

  const handleNotificationCreated = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["notifications"] });
  }, [queryClient]);

  const handleAuditEventCreated = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["audit"] });
    void queryClient.invalidateQueries({ queryKey: ["scheduled-task-history"] });
  }, [queryClient]);

  useEffect(() => {
    if (!isAuthenticated || isLoading || !userId) {
      return;
    }

    const connection = new HubConnectionBuilder()
      .withUrl(SIGNALR_HUB_URL, {
        accessTokenFactory: () => getAccessToken(),
      })
      .withAutomaticReconnect([1000, 2000, 4000, 8000, 16000, 30000])
      .configureLogging(LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    connection.on("PIMSessionStatusChanged", handlePIMSessionStatusChanged);
    connection.on(
      "GDAPRelationshipStatusChanged",
      handleGDAPRelationshipStatusChanged,
    );
    connection.on("SystemHealthChanged", handleSystemHealthChanged);
    connection.on("NotificationCreated", handleNotificationCreated);
    connection.on("AuditEventCreated", handleAuditEventCreated);

    connection.onreconnecting(() => {
      setConnectionState("reconnecting");
    });

    connection.onreconnected(() => {
      setConnectionState("connected");
      // Fetch missed notifications on reconnect
      void queryClient.invalidateQueries({ queryKey: ["notifications"] });
    });

    connection.onclose(() => {
      setConnectionState("disconnected");
    });

    connection
      .start()
      .then(() => {
        setConnectionState("connected");
      })
      .catch(() => {
        setConnectionState("disconnected");
      });

    return () => {
      void connection.stop();
      connectionRef.current = null;
    };
  }, [
    isAuthenticated,
    isLoading,
    userId,
    getAccessToken,
    queryClient,
    handlePIMSessionStatusChanged,
    handleGDAPRelationshipStatusChanged,
    handleSystemHealthChanged,
    handleNotificationCreated,
    handleAuditEventCreated,
  ]);

  return (
    <SignalRContext.Provider value={{ connectionState }}>
      {children}
    </SignalRContext.Provider>
  );
}

/**
 * Access SignalR connection state from any component.
 */
export function useSignalRState(): SignalRConnectionState {
  return useContext(SignalRContext).connectionState;
}
